import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Settings, Mail, Send, Plus, X, Clock, Globe, Copy, CheckCircle, Code, Database, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/utils";
import { ArchiveSettings } from "../components/ArchiveSettings";

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Email settings
  const [dailySummaryEnabled, setDailySummaryEnabled] = useState(false);
  const [summaryTime, setSummaryTime] = useState("08:00");
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");

  // Endpoint info
  const [endpointInfo, setEndpointInfo] = useState(null);
  const [copied, setCopied] = useState("");

  const fetchSettings = useCallback(async () => {
    try {
      const [emailRes, endpointRes] = await Promise.all([
        api.get(`/api/settings/email`),
        api.get(`/api/settings/endpoint`)
      ]);
      setDailySummaryEnabled(emailRes.data.daily_summary_enabled || false);
      setSummaryTime(emailRes.data.summary_time || "08:00");
      setRecipientEmails(emailRes.data.recipient_emails || []);
      setEndpointInfo(endpointRes.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/api/settings/email`, {
        daily_summary_enabled: dailySummaryEnabled,
        summary_time: summaryTime,
        recipient_emails: recipientEmails,
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save settings";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (recipientEmails.length === 0) {
      toast.error("Please add at least one recipient email");
      return;
    }

    setSending(true);
    try {
      await api.post(`/api/email/send-summary`);
      toast.success("Summary email sent successfully");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send email";
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const addRecipient = () => {
    if (!newEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (recipientEmails.includes(newEmail)) {
      toast.error("Email already added");
      return;
    }
    setRecipientEmails([...recipientEmails, newEmail]);
    setNewEmail("");
  };

  const removeRecipient = (email) => {
    setRecipientEmails(recipientEmails.filter((e) => e !== email));
  };

  const handleExportDatabase = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/api/fids/export`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `flights_export_${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Database export downloaded");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to export database";
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto p-6 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="h-8 w-48 bg-muted rounded animate-shimmer mb-8" />
            <Card className="bg-card/50 border-border">
              <CardContent className="p-6">
                <div className="space-y-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-12 bg-muted rounded animate-shimmer" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 md:p-8" data-testid="settings-page">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              SETTINGS
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure system settings and integration endpoints
            </p>
          </div>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="email" data-testid="tab-email">
                <Mail className="h-4 w-4 mr-2" />
                Email Settings
              </TabsTrigger>
              <TabsTrigger value="endpoint" data-testid="tab-endpoint">
                <Globe className="h-4 w-4 mr-2" />
                Integration Endpoint
              </TabsTrigger>
              <TabsTrigger value="database" data-testid="tab-database">
                <Database className="h-4 w-4 mr-2" />
                Database
              </TabsTrigger>
            </TabsList>

            {/* Email Settings Tab */}
            <TabsContent value="email">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Email Notifications
                  </CardTitle>
                  <CardDescription>
                    Configure daily summary email settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="daily-summary" className="text-base">
                        Daily Summary Email
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Receive a daily summary of flight data
                      </p>
                    </div>
                    <Switch
                      id="daily-summary"
                      checked={dailySummaryEnabled}
                      onCheckedChange={setDailySummaryEnabled}
                      data-testid="daily-summary-toggle"
                    />
                  </div>

                  {/* Summary Time */}
                  <div className="space-y-2">
                    <Label htmlFor="summary-time" className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Summary Time (Malaysia/KL - GMT+8)
                    </Label>
                    <Input
                      id="summary-time"
                      type="time"
                      value={summaryTime}
                      onChange={(e) => setSummaryTime(e.target.value)}
                      className="w-40 bg-background"
                      disabled={!dailySummaryEnabled}
                      data-testid="summary-time-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      The daily summary email will be sent automatically at this time every day (Kuala Lumpur timezone, GMT+8).
                    </p>
                  </div>

                  {/* Recipients */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Recipient Emails
                    </Label>
                    
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                        className="bg-background"
                        data-testid="new-recipient-input"
                      />
                      <Button type="button" variant="outline" onClick={addRecipient} data-testid="add-recipient-btn">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {recipientEmails.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">No recipients added</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {recipientEmails.map((email) => (
                          <Badge key={email} variant="secondary" className="pl-3 pr-1 py-1.5">
                            <span className="mr-2">{email}</span>
                            <button
                              onClick={() => removeRecipient(email)}
                              className="hover:bg-background/50 rounded p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-border">
                    <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
                      <Settings className="h-4 w-4 mr-2" />
                      {saving ? "Saving..." : "Save Settings"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSendTestEmail}
                      disabled={sending || recipientEmails.length === 0}
                      data-testid="send-test-email-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sending ? "Sending..." : "Send Test Email"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Integration Endpoint Tab */}
            <TabsContent value="endpoint">
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    MAHB Integration Endpoint
                  </CardTitle>
                  <CardDescription>
                    Provide this endpoint information to MAHB for XML data transmission
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {endpointInfo && (
                    <>
                      {/* Production Endpoint URL */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                          Production Endpoint (for MAHB)
                        </Label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-muted px-4 py-3 rounded-lg font-mono text-sm break-all">
                            https://flightcore.cuvano.com/api/flights/ingest
                          </code>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyToClipboard("https://flightcore.cuvano.com/api/flights/ingest", "url")}
                            data-testid="copy-endpoint-url"
                          >
                            {copied === "url" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Use this URL when configuring the integration in production environment.
                        </p>
                      </div>

                      {/* Method & Content Type */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                            HTTP Method
                          </Label>
                          <Badge variant="default" className="text-lg px-4 py-2">
                            {endpointInfo.method}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                            Content-Type
                          </Label>
                          <Badge variant="outline" className="text-lg px-4 py-2 font-mono">
                            {endpointInfo.content_type}
                          </Badge>
                        </div>
                      </div>

                      {/* Authentication */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                          Authentication
                        </Label>
                        <p className="text-sm">{endpointInfo.authentication}</p>
                      </div>

                      {/* Supported Message Codes */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                          Supported Message Codes
                        </Label>
                        <ScrollArea className="h-[200px] rounded-lg border border-border">
                          <div className="p-4 space-y-2">
                            {endpointInfo.supported_message_codes.map((code, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <Badge variant="outline" className="font-mono text-xs shrink-0">
                                  {code.split(" - ")[0]}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  {code.split(" - ")[1]}
                                </span>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>

                      {/* Sample Request */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                            <Code className="h-4 w-4" />
                            Sample XML Request
                          </Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(endpointInfo.sample_request, "sample")}
                          >
                            {copied === "sample" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                            <span className="ml-1">Copy</span>
                          </Button>
                        </div>
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs font-mono">
                          {endpointInfo.sample_request}
                        </pre>
                      </div>

                      {/* Notes */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                          Important Notes
                        </Label>
                        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                          {endpointInfo.notes.map((note, idx) => (
                            <li key={idx}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Database Tab */}
            <TabsContent value="database" className="space-y-6">
              <ArchiveSettings />
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Database Export
                  </CardTitle>
                  <CardDescription>
                    Download the complete flights history (live + archive) as a single JSON file
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This exports every document in the <code className="font-mono">flights</code> and
                    <code className="font-mono ml-1">flights_archive</code> collections as one downloadable JSON file.
                    The export may be large depending on the amount of ingested data.
                  </p>
                  <Button onClick={handleExportDatabase} disabled={exporting} data-testid="export-database-btn">
                    <Download className="h-4 w-4 mr-2" />
                    {exporting ? "Preparing download..." : "Export Flights Database (JSON)"}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
